import React, { useState } from 'react';
import { processTestPayment } from '../lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { CreditCard, Lock, AlertCircle } from 'lucide-react';

const PaymentForm = ({ orderId, amount, onSuccess, onError }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardNumber, setCardNumber] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [cardError, setCardError] = useState(null);

  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];
    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }
    if (parts.length) {
      return parts.join(' ');
    } else {
      return value;
    }
  };

  const formatExpiry = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    if (v.length >= 2) {
      return v.substring(0, 2) + '/' + v.substring(2, 4);
    }
    return v;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const cleanCardNumber = cardNumber.replace(/\s/g, '');
    
    // Validate inputs
    if (!cleanCardNumber || cleanCardNumber.length < 4) {
      setCardError('Ingresa un número de tarjeta válido');
      return;
    }

    if (!expiryDate || expiryDate.length < 5) {
      setCardError('Ingresa una fecha de vencimiento válida (MM/YY)');
      return;
    }

    if (!cvv || cvv.length < 3) {
      setCardError('Ingresa un CVV válido');
      return;
    }

    setIsProcessing(true);
    setCardError(null);

    try {
      // Check if it's a test card number
      const testCards = ['0000', '4242', '0000000000000000', '4242424242424242'];
      
      if (testCards.includes(cleanCardNumber)) {
        // Process as test payment
        const response = await processTestPayment(orderId, cleanCardNumber);
        if (response.data.status === 'success') {
          onSuccess({ id: 'test_payment', status: 'succeeded' });
        } else {
          throw new Error('Error al procesar el pago de prueba');
        }
      } else {
        // For real cards, show message that Stripe is not configured
        setCardError('El procesador de pagos no está configurado. Usa "0000" como número de tarjeta para modo de prueba.');
        setIsProcessing(false);
        return;
      }
    } catch (err) {
      console.error('Payment error:', err);
      const errorMessage = err.response?.data?.detail || 'Error al procesar el pago';
      setCardError(errorMessage);
      onError(errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatPrice = (price) => {
    return `L. ${price.toLocaleString('es-HN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Test Mode Notice */}
      <div className="p-3 rounded-xl bg-yellow-500/10 border border-yellow-500/30 flex items-start gap-2">
        <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
        <div className="text-sm">
          <p className="font-medium text-yellow-600">Modo de Prueba</p>
          <p className="text-yellow-600/80">Usa <strong>0000</strong> como número de tarjeta para completar el pago de prueba.</p>
        </div>
      </div>

      {/* Card Number */}
      <div>
        <Label htmlFor="card-number">Número de Tarjeta</Label>
        <div className="relative mt-1">
          <Input
            id="card-number"
            type="text"
            value={cardNumber}
            onChange={(e) => setCardNumber(formatCardNumber(e.target.value))}
            placeholder="0000 0000 0000 0000"
            maxLength={19}
            className="pl-10"
            data-testid="card-number-input"
          />
          <CreditCard className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        </div>
      </div>

      {/* Expiry and CVV */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label htmlFor="expiry">Fecha de Vencimiento</Label>
          <Input
            id="expiry"
            type="text"
            value={expiryDate}
            onChange={(e) => setExpiryDate(formatExpiry(e.target.value))}
            placeholder="MM/YY"
            maxLength={5}
            className="mt-1"
            data-testid="card-expiry-input"
          />
        </div>
        <div>
          <Label htmlFor="cvv">CVV</Label>
          <div className="relative mt-1">
            <Input
              id="cvv"
              type="text"
              value={cvv}
              onChange={(e) => setCvv(e.target.value.replace(/\D/g, '').substring(0, 4))}
              placeholder="123"
              maxLength={4}
              className="pr-10"
              data-testid="card-cvv-input"
            />
            <Lock className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          </div>
        </div>
      </div>

      {/* Error Message */}
      {cardError && (
        <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-500 text-sm">
          {cardError}
        </div>
      )}

      {/* Submit Button */}
      <Button
        type="submit"
        className="w-full btn-primary h-14 text-lg"
        disabled={isProcessing}
        data-testid="pay-card-button"
      >
        {isProcessing ? (
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            Procesando...
          </div>
        ) : (
          <>
            <Lock className="w-4 h-4 mr-2" />
            Pagar {formatPrice(amount)}
          </>
        )}
      </Button>

      {/* Security Badge */}
      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <Lock className="w-3 h-3" />
        <span>Pago seguro</span>
      </div>
    </form>
  );
};

export default PaymentForm;
